// @flow
import { Map } from 'immutable';
import F from 'lodash/fp';
import PropTypes from 'prop-types';
import * as React from 'react';
import _ from 'lodash';

import { Animated } from 'react-animated-css';
import { AvatarContext } from '@sonyged/ext-proc';
import { Text } from '@sonyged/proc-ui';

import { ArrowIcon, LossColor } from '../../../../common/components/ArrowIcon';
import { type IceCount, IceCountIndicator } from '../../ice/fields/SimulationContent/IceCount';
import { PriceSlider } from '../../ice/fields/PriceSlider';
import { Weather } from '../../ice/fields/SimulationContent/Weather';
import AppContext from '../../../AppContext';
import Background from './Background';
import Constants from '../../ice/fields/Constants';
import CustomerSimulation from '../../ice/fields/SimulationContent/CustomerSimulation';
import Grade1Image from '../../../../assets/images/pages/ice/fields/ice_field_grade_1@3x.png';
import Grade2Image from '../../../../assets/images/pages/ice/fields/ice_field_grade_2@3x.png';
import Grade3Image from '../../../../assets/images/pages/ice/fields/ice_field_grade_3@3x.png';

type HourMin = [number, number];
type CustomerInfo = { type: number, price: number };

const ONE_MINUTE = Constants.SIMULATION_TIME_MSEC / (Constants.END_HOUR - Constants.START_HOUR) / 60.0;

// convert to simulated hour - minute
const deltaToHm = (delta: number): HourMin => {
  const elapsedMinute = Math.floor(delta / ONE_MINUTE);
  const elapsedHour = Math.floor(elapsedMinute / 60);

  const hour = Constants.START_HOUR + elapsedHour;
  const min = elapsedMinute % 60;

  if (hour >= Constants.END_HOUR) return [Constants.END_HOUR, 0];
  return [hour, min];
};

// get all [hour, min] between from and to, not including to
const hourMinRange = ([fromH, fromM]: HourMin, [toH, toM]: HourMin): HourMin[] => {
  if (fromH > toH) return [];

  if (fromH === toH) return _.range(fromM, toM).map(m => [fromH, m]);

  return _.concat(
    _.range(fromM, 60).map(m => [fromH, m]), // same hour as fromH
    _.flatMap(_.range(fromH + 1, toH), h => _.range(0, 60).map(m => [h, m])), // in between
    _.range(0, toM).map(m => [toH, m]) // same hour as toH
  );
};

// get customer data for specific hour-min
const customerSlice = (customers: CustomerInfo[][], [hour, min]: HourMin) => {
  const customersForHour = customers[hour - Constants.START_HOUR];
  const chunkSize = Math.ceil(customersForHour.length / 60);
  const customerChunks = F.range(0, 60).map(m => customersForHour.slice(m * chunkSize, (m + 1) * chunkSize));
  return customerChunks[min];
};

const DESIGN_WIDTH = 1440;
const DESIGN_HEIGHT = 840;

type IceContext = {
  weather: number,
  place: number,
  ice: number,
  price: number,
  stockNum: number,
  temperature: number,
  totalSales: number,
};

type Props = {
  research: boolean,
  iceCtx: IceContext,
  wagon: number,
  onNextClick: () => void,
  onPriceChange: number => void,
  // onStockNumChange: number => void,
  onUpdateResults: (Array<{}>) => void,
  customers: Array<Array<{ type: number, price: number }>>,
};

type State = {
  scale: number,
  width: number,
  height: number,
  offsetLeft: number,
  totalPrice: number,
  hour: number,
  min: number,
  stockNum: number,
  soldCount: number,
  message: string | null,
  clockCounter: number,
  clockAlertMessage: React.Node,
  init: boolean,
  finish: boolean,
  iceCounts: IceCount[],

  customerCounts: Map<string, number>,
  startTs: ?number,
};

export default class SimulationContent extends React.Component<Props, State> {
  viewport: ?HTMLDivElement;
  timeLapsed = 0;
  lastPrice = 0;
  originalStockNum = 0;

  static contextType = AppContext;
  static defaultProps = { research: false };

  constructor(props: Props) {
    super(props);
    this.state = {
      totalPrice: 0,
      hour: Constants.START_HOUR,
      min: 0,
      // stockNum: Constants.STOCK_NUM,
      stockNum: props.iceCtx.stockNum,
      message: null,
      clockCounter: 0,
      clockAlertMessage: null,
      soldCount: 0,
      iceCounts: [],
      width: 0,
      height: 0,
      offsetLeft: 0,
      scale: 1,
      init: true,
      finish: false,

      customerCounts: Map(),
      startTs: null,
    };

    (this: any).calculateAnimationAreaSize = this.calculateAnimationAreaSize.bind(this);

    this.originalStockNum = props.iceCtx.stockNum;
  }

  componentDidMount() {
    this.context.soundController.sePlay('proc_start');

    setTimeout(() => {
      setTimeout(() => {
        requestAnimationFrame(this.loop);
      }, ONE_MINUTE);

      this.setState({ init: false, startTs: Date.now() });
    }, 3000);

    window.addEventListener('resize', this.calculateAnimationAreaSize);

    this.calculateAnimationAreaSize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.calculateAnimationAreaSize);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    if (nextState !== this.state) return true;
    // We only care for price change
    if (nextProps.iceCtx.price !== this.props.iceCtx.price) return true;
    return false;
  }

  calculateAnimationAreaSize() {
    if (!this.viewport) return;

    const vw = this.viewport.clientWidth;
    const vh = this.viewport.clientHeight;

    const scaleW = vw / DESIGN_WIDTH;
    const scaleH = vh / DESIGN_HEIGHT;

    const scale = Math.max(scaleW, scaleH);

    const width = DESIGN_WIDTH * scale;
    const height = DESIGN_HEIGHT * scale;

    const offsetLeft = (vw - width) / 2;

    this.setState({ width, height, offsetLeft, scale });
  }

  isSoldout(): boolean {
    return this.state.stockNum == 0;
  }

  render() {
    return (
      <div className="proc-ice-field-content simulation relative h100 white-bg-color">
        {this._renderHeader()}
        {this._renderContent()}
        {this._renderStartModal()}
        {this._renderFinishModal()}
        {this._renderMessage()}
        {this._renderPrice()}
      </div>
    );
  }

  _renderHeader() {
    let clockStyle = {
      backgroundPosition: `calc(${this._calcClockPosition()}%)`,
    };
    return (
      <div className={'header white-bg-color'}>
        <div className={'proc-outer'}>
          <div className={'ice-field-status-container'}>
            <div className={'left'}>
              <div className={'weather secondary100-fill-color'}>
                {Constants.getWetherSvg(this.props.iceCtx.weather)}
              </div>
              <div className={'temperature secondary100-color'}>
                <span className={'proc-huge-font'}>{this.props.iceCtx.temperature}</span>
                <span className={'proc-medium-font'}>度</span>
              </div>
            </div>
            <div className={'center'}>
              <div className={'price-container center-aligned'}>
                <div className={'ice-container'}>
                  <img className={'ice-image'} src={Constants.getIceImage(this.props.iceCtx.ice)} />
                  <div className={'stock-blocks-container'}>{this._renderIceStocks()}</div>
                  <div className={'proc-medium-font gray30-color stock-num'}>{this.state.stockNum}</div>
                </div>
                <div className={'total-price-container primary40-bg-color'}>
                  <div className={'total-price gray100-color'}>
                    <img className={'coin'} src={'../../images/pages/ice/fields/img-ice-field-simulation-coin.png'} />
                    {Math.floor(this.state.totalPrice)}
                  </div>
                </div>
              </div>
            </div>
            <div className={'right'}>
              <div className="clock relative" style={clockStyle}>
                {this.state.clockAlertMessage && (
                  <Animated
                    className="clock-alert secondary300-bg-color"
                    animationIn="fadeIn"
                    animationOut="fadeOut"
                    isVisible={true}
                  >
                    <div className={'center-aligned text proc-medium-font white-color'}>
                      {this.state.clockAlertMessage}
                    </div>
                  </Animated>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  _renderIceStocks(): any {
    let blockNum = 20;
    let blocks = [];
    let activeBlockNum = this.state.stockNum / (this.props.iceCtx.stockNum / blockNum);
    for (let i = 0; i < blockNum; i++) {
      blocks.push(i < activeBlockNum);
    }
    return blocks.map(function(b, i) {
      return (
        <div key={`index-${i}`} className={`stock-block ${b ? 'active' : ''} ${5 < activeBlockNum ? '' : 'few'}`} />
      );
    });
  }

  _renderContent() {
    const delta = (Constants.END_HOUR - Constants.START_HOUR) / 5;
    const colorIndex = 1 + Math.floor((this.state.hour - Constants.START_HOUR) / delta);

    const animationAreaStyle = {
      width: this.state.width + 'px',
      height: this.state.height + 'px',
      transform: `translate3d(${this.state.offsetLeft}px, 0, 0)`,
    };

    return (
      <div
        className={`content-simulation`}
        ref={r => {
          this.viewport = r;
        }}
      >
        <div className="animation-area" style={animationAreaStyle}>
          <Background
            colorIndex={colorIndex}
            weather={this.props.iceCtx.weather}
            place={this.props.iceCtx.place}
            wagon={this.props.wagon}
            scale={this.state.scale}
            soldout={this.isSoldout()}
          />
          {!this.state.init && (
            <CustomerSimulation customerCounts={this.state.customerCounts} scale={this.state.scale} />
          )}
          <Weather weather={this.props.iceCtx.weather} />
          <IceCountIndicator
            scale={this.state.scale}
            iceCounts={this.state.iceCounts}
            onAnimationEnd={this._onIceCountAnimationEnd.bind(this)}
          />
        </div>
      </div>
    );
  }

  _renderStartModal() {
    if (!this.state.init) {
      return <span />;
    }

    return (
      <div className={'start-view-container proc-border-radius center-aligned secondary20-border-color'}>
        <div className={'background absolute white-bg-color'} />
        <div className={`contents ${this.props.research ? 'research' : ''}`}>
          <AvatarContext.Consumer>
            {avatar => {
              return <img className="image" src={Constants.getStartImage(avatar ? avatar : 1)} />;
            }}
          </AvatarContext.Consumer>
          <div className={'title proc-large-font secondary100-color'}>スタート！</div>
          {!this.props.research && (
            <div className={'desc proc-medium-font gray100-color'}>売るねだんはとちゅうでかえられるよ。</div>
          )}
        </div>
      </div>
    );
  }

  _renderFinishModal() {
    if (!this.state.finish) {
      return <span />;
    }

    let { totalPrice } = this.state;
    let { iceCtx } = this.props;
    let profit = totalPrice - iceCtx.stockNum * Constants.DEFAULT_PRICE;
    let text = profit >= 0 ? 'もうけたお金' : 'そんしたお金';
    let image;
    if (profit < 0) {
      image = Grade3Image;
    } else if (profit < 5000) {
      image = Grade2Image;
    } else {
      image = Grade1Image;
    }

    return (
      <div className={'finish-view-container proc-border-radius center-aligned secondary20-border-color'}>
        <div className={'background absolute white-bg-color'} />
        <div className={'contents'}>
          <div className={'title proc-small-font secondary100-color'}>{text}</div>
          <div className={'desc proc-huge-font'}>
            <Text color={profit < 0 ? LossColor : null}>{Math.abs(profit)}</Text>
            <Text color={profit < 0 ? LossColor : null} className="postfix">
              {' 円 '}
            </Text>
            <span className="postfix">
              <ArrowIcon forNum={profit} />
            </span>
          </div>
          <img className={'image'} src={image} />
        </div>
      </div>
    );
  }

  _renderMessage() {
    if (!this.state.message) {
      return <span />;
    }

    return (
      <AvatarContext.Consumer>
        {avatar => {
          return (
            <Animated
              className={`error-container ${this.props.research ? 'research' : ''}`}
              animationIn="fadeIn"
              animationOut="fadeOut"
              isVisible={true}
            >
              <div
                className={
                  'text proc-medium-font secondary20-bg-color gray100-color secondary20-after-border-left-color'
                }
              >
                {this.state.message}
              </div>
              <img className={'error-image'} src={Constants.getCommentImage(avatar ? avatar : 1)} />
            </Animated>
          );
        }}
      </AvatarContext.Consumer>
    );
  }

  _renderPrice() {
    if (this.props.research) {
      return (
        <table className={'proc-table simulation research'}>
          <thead>
            <tr>
              <th>ねだん</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="white-bg-color proc-huge-font">{this.props.iceCtx.price} えん</td>
            </tr>
          </tbody>
        </table>
      );
    }

    return (
      <table className={'proc-table simulation'}>
        <thead>
          <tr>
            <th>ねだん</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="white-bg-color">
              <PriceSlider
                value={this.props.iceCtx.price}
                min={Constants.MIN_PRICE}
                max={Constants.MAX_PRICE}
                step={Constants.PRICE_STEP}
                onChange={v => {
                  this.context.soundController.sePlay('nedan_bar');
                  this.props.onPriceChange(v);
                }}
                disabled={this.isSoldout()}
              />
              {this.isSoldout() && (
                <img
                  src={Constants.getSoldoutImageAdv(this.props.wagon)}
                  style={{
                    position: 'absolute',
                    bottom: 5,
                    width: 154,
                    height: 80,
                    left: 0,
                    right: 0,
                    margin: '0 auto',
                  }}
                />
              )}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  update = (timestamp: number) => (state: State): State => {
    const {
      customers,
      iceCtx: { price },
    } = this.props;
    // let {
    //   iceCtx: { stockNum },
    // } = this.props;
    const { startTs, hour, min, totalPrice, customerCounts } = state;
    let { stockNum, clockCounter, soldCount } = state;

    const delta = timestamp - (startTs || timestamp);
    const [newHour, newMin] = deltaToHm(delta);

    // sale
    const ranges = hourMinRange([hour, min], [newHour, newMin]);

    clockCounter += ranges.length;

    let totalSale = 0;
    let results = [];
    for (const range of ranges) {
      const customerList = customerSlice(customers, range);
      let customerData = new Array(Constants.CUSTOMER_LENGTH).fill(0);
      let purchasersData = new Array(Constants.CUSTOMER_LENGTH).fill(0);
      let sale = 0;

      for (const customer of customerList) {
        customerData[customer.type] += 1;

        if (stockNum > 0 && price <= customer.price) {
          sale += price;
          stockNum--;
          // this.props.onStockNumChange(stockNum--);
          soldCount++;
          purchasersData[customer.type] += 1;

          if (stockNum === 0) {
            this.context.soundController.sePlay('soldout_3');
          }
        }
      }

      const [h, m] = range;

      const result = {
        salesData: { hour: h, price: sale },
        customersData: { hour: h, data: customerData },
        purchasersData: { hour: h, data: purchasersData },
        priceTransitionData: { hour: h, min_step: m / 10, price: price },
      };

      results.push(result);

      totalSale += sale;
    }

    this.props.onUpdateResults(results);

    let nextState = {
      ...state,
      hour: newHour,
      min: newMin,
      totalPrice: totalPrice + totalSale,
      stockNum,
      soldCount,
      clockCounter,
    };

    // show sold count each 0.5s or when price changed
    this.timeLapsed += (newHour * 60 + newMin - hour * 60 - min) * ONE_MINUTE;
    if (this.timeLapsed >= 500 || this.lastPrice !== price) {
      this.timeLapsed = 0;
      this.lastPrice = price;

      if (nextState.soldCount > 0) {
        nextState.iceCounts.push({
          price,
          count: soldCount,
          ts: Date.now(),
        });
        this.context.soundController.sePlay('coin01');
        nextState.soldCount = 0;
      }
    }

    // customer count update
    let updatedCustomerCount: { [string]: number } = {};
    if (newHour < Constants.END_HOUR) {
      const countByType = F.pipe(F.prop(newHour - Constants.START_HOUR), F.countBy('type'))(customers);

      const allCustomerKinds = ['0', '1', '2'];
      updatedCustomerCount = F.pipe(
        F.map(type => [type, Math.ceil(F.propOr(0, type, countByType) / 3)]),
        F.fromPairs
      )(allCustomerKinds);
    }

    this._checkMessage(nextState);

    const finish = newHour >= Constants.END_HOUR;
    if (!this.state.finish && finish) {
      this.context.soundController.sePlay('ice_stamp');
    }

    return {
      ...nextState,
      finish,
      customerCounts: customerCounts.merge(updatedCustomerCount),
    };
  };

  loop = () => {
    // Update state
    const now = Date.now();
    this.setState(this.update(now), () => {
      if (this.state.finish) {
        setTimeout(this.props.onNextClick, 5000);

        return;
      }

      // schedule to run again
      setTimeout(() => {
        requestAnimationFrame(this.loop);
      }, ONE_MINUTE);
    });
  };

  _checkMessage(nextState: State) {
    // check status when the remaining time became 3/4.
    if (!nextState.message) {
      if (nextState.hour === (Constants.END_HOUR - Constants.START_HOUR) / 4 + Constants.START_HOUR) {
        if (nextState.stockNum <= 50) {
          nextState.message = 'すぐに売り切れそう。';
        }
        // check status when the remaining time became 1/4.
      } else if (nextState.hour === ((Constants.END_HOUR - Constants.START_HOUR) / 4) * 3 + Constants.START_HOUR) {
        if (nextState.stockNum <= 0) {
          nextState.message = '安くしすぎたかな。';
        } else if (nextState.stockNum >= 50) {
          nextState.message = 'たくさんのこっちゃいそう。';
        }
      }
      if (nextState.message) {
        if (!this.state.message) {
          this.context.soundController.sePlay('hint02');
        }
        setTimeout(() => {
          this.setState({ message: null });
        }, 5000);
      }
    }

    // show clock alert message when the remaining time became half.
    if (!nextState.clockAlertMessage) {
      if (nextState.hour === (Constants.END_HOUR - Constants.START_HOUR) / 2 + Constants.START_HOUR) {
        nextState.clockAlertMessage = (
          <span>
            のこり
            <br />
            半分
          </span>
        );
      } else if (nextState.hour === ((Constants.END_HOUR - Constants.START_HOUR) / 4) * 3 + Constants.START_HOUR) {
        nextState.clockAlertMessage = (
          <span>
            あと
            <br />
            少し
          </span>
        );
      }
      if (nextState.clockAlertMessage) {
        if (!this.state.clockAlertMessage) {
          this.context.soundController.sePlay('hint02');
        }
        setTimeout(() => {
          this.setState({ clockAlertMessage: null });
        }, 5000);
      }
    }
  }

  _onIceCountAnimationEnd(iceCount: *) {
    const index = this.state.iceCounts.indexOf(iceCount);
    if (index >= 0) {
      this.state.iceCounts.splice(index, 1);
    }
  }

  _calcClockPosition() {
    let totalMsec = Constants.SIMULATION_TIME_MSEC;
    let eachMsec = totalMsec / (Constants.END_HOUR - Constants.START_HOUR) / 60.0;
    let totalNum = totalMsec / eachMsec;
    return (100 / 59) * Math.floor((this.state.clockCounter * 59) / totalNum);
  }
}

SimulationContent.propTypes = {
  iceCtx: PropTypes.object,
  research: PropTypes.bool,
  onNextClick: PropTypes.func,
  onPriceChange: PropTypes.func,
  onStockNumChange: PropTypes.func,
  onUpdateResults: PropTypes.func,
  customers: PropTypes.array,
};
